import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import slugify from 'slugify';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  summary: string;
  content: string;
  featured_image?: string;
  published: boolean;
  author_id: string;
  category_id?: string;
  view_count: number;
  published_at?: string;
  featured: boolean;
  meta_title?: string;
  meta_description?: string;
  created_at: string;
  updated_at: string;
  author?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  category?: {
    name: string;
    slug: string;
  };
  tags?: BlogTag[];
  comments_count?: number;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  post_count?: number;
}

export interface BlogTag {
  id: string;
  name: string;
  slug: string;
  post_count?: number;
}

export interface BlogComment {
  id: string;
  post_id: string;
  content: string;
  user_id?: string;
  guest_name?: string;
  guest_email?: string;
  approved: boolean;
  parent_id?: string;
  created_at: string;
  updated_at: string;
  user?: {
    first_name?: string;
    last_name?: string;
  };
  replies?: BlogComment[];
}

export interface BlogPostInput {
  title: string;
  summary: string;
  content: string;
  category_id?: string;
  featured_image?: string;
  published?: boolean;
  featured?: boolean;
  meta_title?: string;
  meta_description?: string;
  tags?: string[];
}

// Get published blog posts with pagination
export const getPublishedPosts = async (
  page = 1,
  pageSize = 10,
  categorySlug?: string,
  tagSlug?: string,
  searchQuery?: string
) => {
  let query = supabase
    .from('blog_posts')
    .select(`
      *,
      category:category_id(name, slug)
    `)
    .eq('published', true)
    .not('published_at', 'is', null)
    .order('published_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (categorySlug) {
    const { data: category } = await supabase
      .from('blog_categories')
      .select('id')
      .eq('slug', categorySlug)
      .single();
    
    if (category) {
      query = query.eq('category_id', category.id);
    }
  }

  if (tagSlug) {
    const { data: tag } = await supabase
      .from('blog_tags')
      .select('id')
      .eq('slug', tagSlug)
      .single();
    
    if (tag) {
      const { data: postIds } = await supabase
        .from('blog_post_tags')
        .select('post_id')
        .eq('tag_id', tag.id);
      
      if (postIds && postIds.length > 0) {
        query = query.in('id', postIds.map(p => p.post_id));
      } else {
        return { data: [], count: 0 };
      }
    }
  }

  if (searchQuery) {
    query = query.or(`title.ilike.%${searchQuery}%, content.ilike.%${searchQuery}%`);
  }

  const { data, error } = await query;

  // Get the total count for pagination
  const { count, error: countError } = await supabase
    .from('blog_posts')
    .select('id', { count: 'exact', head: true })
    .eq('published', true)
    .not('published_at', 'is', null);

  if (error || countError) {
    console.error('Error fetching blog posts:', error || countError);
    return { data: [], count: 0 };
  }

  // Fetch additional data for each post
  if (data && data.length > 0) {
    for (const post of data) {
      // Get author information from user_profiles
      if (post.author_id) {
        const { data: authorData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', post.author_id)
          .single();
          
        if (authorData) {
          post.author = {
            first_name: authorData.first_name,
            last_name: authorData.last_name
          };
        }
      }
      
      // Fetch tags for each post
      const { data: postTags } = await supabase
        .from('blog_post_tags')
        .select(`tag:tag_id(id, name, slug)`)
        .eq('post_id', post.id);
      
      post.tags = postTags ? postTags.map(pt => pt.tag) : [];

      // Get comment count
      const { count: commentsCount } = await supabase
        .from('blog_comments')
        .select('id', { count: 'exact', head: true })
        .eq('post_id', post.id)
        .eq('approved', true);
      
      post.comments_count = commentsCount || 0;
    }
  }

  return { data: data || [], count: count || 0 };
};

// Get a single blog post by slug
export const getPostBySlug = async (slug: string) => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      category:category_id(name, slug)
    `)
    .eq('slug', slug)
    .single();

  if (error) {
    console.error('Error fetching blog post:', error);
    return null;
  }

  if (data) {
    // Get author information from user_profiles
    if (data.author_id) {
      const { data: authorData } = await supabase
        .from('user_profiles')
        .select('first_name, last_name, email')
        .eq('id', data.author_id)
        .single();
        
      if (authorData) {
        data.author = {
          first_name: authorData.first_name,
          last_name: authorData.last_name,
          email: authorData.email
        };
      }
    }
    
    // Fetch tags for the post
    const { data: postTags } = await supabase
      .from('blog_post_tags')
      .select(`tag:tag_id(id, name, slug)`)
      .eq('post_id', data.id);
    
    data.tags = postTags ? postTags.map(pt => pt.tag) : [];

    // Increment view count
    await supabase.rpc('increment_blog_view_count', { post_id: data.id });
  }

  return data;
};

// Get featured posts
export const getFeaturedPosts = async (limit = 3) => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      category:category_id(name, slug)
    `)
    .eq('published', true)
    .eq('featured', true)
    .order('published_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching featured posts:', error);
    return [];
  }

  // Fetch author information for each post
  if (data && data.length > 0) {
    for (const post of data) {
      if (post.author_id) {
        const { data: authorData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', post.author_id)
          .single();
          
        if (authorData) {
          post.author = {
            first_name: authorData.first_name,
            last_name: authorData.last_name
          };
        }
      }
    }
  }

  return data || [];
};

// Get all categories with post count
export const getAllCategories = async () => {
  const { data, error } = await supabase
    .from('blog_categories')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching categories:', error);
    return [];
  }

  // Get post count for each category
  if (data && data.length > 0) {
    for (const category of data) {
      const { count } = await supabase
        .from('blog_posts')
        .select('id', { count: 'exact', head: true })
        .eq('category_id', category.id)
        .eq('published', true);
      
      category.post_count = count || 0;
    }
  }

  return data || [];
};

// Get all tags with post count
export const getAllTags = async () => {
  const { data, error } = await supabase
    .from('blog_tags')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching tags:', error);
    return [];
  }

  // Get post count for each tag
  if (data && data.length > 0) {
    for (const tag of data) {
      const { data: postTags } = await supabase
        .from('blog_post_tags')
        .select('post_id')
        .eq('tag_id', tag.id);
      
      // Count only posts that are published
      if (postTags && postTags.length > 0) {
        const { count } = await supabase
          .from('blog_posts')
          .select('id', { count: 'exact', head: true })
          .in('id', postTags.map(pt => pt.post_id))
          .eq('published', true);
        
        tag.post_count = count || 0;
      } else {
        tag.post_count = 0;
      }
    }

    // Sort by post count in descending order
    data.sort((a, b) => (b.post_count || 0) - (a.post_count || 0));
  }

  return data || [];
};

// Get comments for a post
export const getPostComments = async (postId: string) => {
  // First get all top-level comments without trying to join with auth.users
  const { data: topLevelComments, error } = await supabase
    .from('blog_comments')
    .select('*')
    .eq('post_id', postId)
    .eq('approved', true)
    .is('parent_id', null)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching comments:', error);
    return [];
  }

  // Then get all replies without trying to join with auth.users
  const { data: replies, error: repliesError } = await supabase
    .from('blog_comments')
    .select('*')
    .eq('post_id', postId)
    .eq('approved', true)
    .not('parent_id', 'is', null)
    .order('created_at', { ascending: true });

  if (repliesError) {
    console.error('Error fetching comment replies:', repliesError);
    return topLevelComments || [];
  }

  // Fetch user information separately for comments and replies
  const fetchUserInfo = async (comments: any[]) => {
    if (!comments || comments.length === 0) return;
    
    for (const comment of comments) {
      if (comment.user_id) {
        // Get user information from user_profiles
        const { data: userData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', comment.user_id)
          .single();
          
        if (userData) {
          comment.user = {
            first_name: userData.first_name,
            last_name: userData.last_name
          };
        }
      }
    }
  };

  // Fetch user info for top-level comments and replies
  if (topLevelComments) await fetchUserInfo(topLevelComments);
  if (replies) await fetchUserInfo(replies);

  // Organize replies under their parent comments
  if (topLevelComments && replies) {
    for (const comment of topLevelComments) {
      comment.replies = replies.filter(reply => reply.parent_id === comment.id);
    }
  }

  return topLevelComments || [];
};

// Add a comment to a post
export const addComment = async (
  postId: string,
  content: string,
  parentId?: string,
  userId?: string,
  guestName?: string,
  guestEmail?: string
) => {
  // Determine if it's a guest or user comment
  const commentData: any = {
    post_id: postId,
    content,
    approved: false, // All comments require approval by default
  };

  if (parentId) {
    commentData.parent_id = parentId;
  }

  if (userId) {
    commentData.user_id = userId;
    // Auto-approve comments from logged in users
    commentData.approved = true;
  } else {
    commentData.guest_name = guestName;
    commentData.guest_email = guestEmail;
  }

  const { data, error } = await supabase
    .from('blog_comments')
    .insert(commentData)
    .select();

  if (error) {
    console.error('Error adding comment:', error);
    return null;
  }

  return data?.[0] || null;
};

// ADMIN FUNCTIONS

// Get all blog posts (admin)
export const getAllPosts = async (page = 1, pageSize = 10) => {
  // Get blog posts without trying to join with auth.users
  const { data, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      category:category_id(name, slug)
    `)
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  const { count, error: countError } = await supabase
    .from('blog_posts')
    .select('id', { count: 'exact', head: true });

  if (error || countError) {
    console.error('Error fetching all blog posts:', error || countError);
    return { data: [], count: 0 };
  }

  // Fetch author information separately for each post
  if (data && data.length > 0) {
    for (const post of data) {
      // Get author information from user_profiles
      if (post.author_id) {
        const { data: authorData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', post.author_id)
          .single();
          
        if (authorData) {
          post.author = {
            first_name: authorData.first_name,
            last_name: authorData.last_name
          };
        }
      }

      // Fetch tags for each post
      const { data: postTags } = await supabase
        .from('blog_post_tags')
        .select(`tag:tag_id(id, name, slug)`)
        .eq('post_id', post.id);
      
      post.tags = postTags ? postTags.map(pt => pt.tag) : [];
    }
  }

  return { data: data || [], count: count || 0 };
};

// Create a new blog post
export const createPost = async (postData: BlogPostInput, authorId: string) => {
  // Generate a unique slug from the title
  const baseSlug = slugify(postData.title, { lower: true, strict: true });
  const timestamp = Date.now();
  const slug = `${baseSlug}-${timestamp}`;

  const newPost = {
    title: postData.title,
    slug,
    summary: postData.summary,
    content: postData.content,
    featured_image: postData.featured_image,
    author_id: authorId,
    category_id: postData.category_id,
    published: postData.published || false,
    featured: postData.featured || false,
    meta_title: postData.meta_title || postData.title,
    meta_description: postData.meta_description || postData.summary,
    published_at: postData.published ? new Date().toISOString() : null
  };

  // Create the post
  const { data, error } = await supabase
    .from('blog_posts')
    .insert(newPost)
    .select();

  if (error) {
    console.error('Error creating blog post:', error);
    return null;
  }

  const post = data?.[0];

  // If post has tags, add them
  if (post && postData.tags && postData.tags.length > 0) {
    for (const tagName of postData.tags) {
      // First check if the tag exists
      let tag;
      const { data: existingTag } = await supabase
        .from('blog_tags')
        .select('id')
        .eq('name', tagName)
        .single();
      
      if (existingTag) {
        tag = existingTag;
      } else {
        // Create the tag if it doesn't exist
        const tagSlug = slugify(tagName, { lower: true, strict: true });
        const { data: newTag, error: tagError } = await supabase
          .from('blog_tags')
          .insert({ name: tagName, slug: tagSlug })
          .select();
        
        if (tagError) {
          console.error('Error creating tag:', tagError);
          continue;
        }
        
        tag = newTag?.[0];
      }

      if (tag) {
        // Associate tag with post
        await supabase
          .from('blog_post_tags')
          .insert({ post_id: post.id, tag_id: tag.id });
      }
    }
  }

  return post;
};

// Update a blog post
export const updatePost = async (id: string, postData: Partial<BlogPostInput>) => {
  // Separate tags from other post data
  const { tags, ...postUpdateData } = postData;
  const updateData: any = { ...postUpdateData };
  
  // If setting post to published and it wasn't before, update published_at date
  if (postData.published) {
    const { data: currentPost } = await supabase
      .from('blog_posts')
      .select('published')
      .eq('id', id)
      .single();
    
    if (currentPost && !currentPost.published) {
      updateData.published_at = new Date().toISOString();
    }
  }

  // Update the post (without the tags property)
  const { data, error } = await supabase
    .from('blog_posts')
    .update(updateData)
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error updating blog post:', error);
    return null;
  }

  const post = data?.[0];

  // If tags are provided, update them
  if (post && tags) { // Check if tags array exists in the original postData
    // First remove all existing tags
    await supabase
      .from('blog_post_tags')
      .delete()
      .eq('post_id', id);
    
    // Then add the new tags
    for (const tagName of tags) {
      // Check if tag exists
      let tag;
      const { data: existingTag } = await supabase
        .from('blog_tags')
        .select('id')
        .eq('name', tagName)
        .single();
      
      if (existingTag) {
        tag = existingTag;
      } else {
        // Create tag if it doesn't exist
        const tagSlug = slugify(tagName, { lower: true, strict: true });
        const { data: newTag } = await supabase
          .from('blog_tags')
          .insert({ name: tagName, slug: tagSlug })
          .select();
        
        tag = newTag?.[0];
      }

      if (tag) {
        // Associate tag with post
        await supabase
          .from('blog_post_tags')
          .insert({ post_id: id, tag_id: tag.id });
      }
    }
  }

  return post;
};

// Delete a blog post
export const deletePost = async (id: string) => {
  // Delete the post (this will cascade delete the post-tag relationships due to FK constraints)
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting blog post:', error);
    return false;
  }

  return true;
};

// Category management
export const createCategory = async (name: string, description?: string) => {
  const slug = slugify(name, { lower: true, strict: true });
  
  const { data, error } = await supabase
    .from('blog_categories')
    .insert({ name, slug, description })
    .select();

  if (error) {
    console.error('Error creating category:', error);
    return null;
  }

  return data?.[0] || null;
};

export const updateCategory = async (id: string, name: string, description?: string) => {
  const slug = slugify(name, { lower: true, strict: true });
  
  const { data, error } = await supabase
    .from('blog_categories')
    .update({ name, slug, description })
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error updating category:', error);
    return null;
  }

  return data?.[0] || null;
};

export const deleteCategory = async (id: string) => {
  const { error } = await supabase
    .from('blog_categories')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting category:', error);
    return false;
  }

  return true;
};

// Comment management
export const getAllComments = async (page = 1, pageSize = 20, approvedOnly = false) => {
  // Get comments without trying to join with auth.users
  let query = supabase
    .from('blog_comments')
    .select(`
      *,
      post:post_id(title, slug)
    `)
    .order('created_at', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1);

  if (approvedOnly) {
    query = query.eq('approved', true);
  }

  const { data, error } = await query;

  const { count, error: countError } = await supabase
    .from('blog_comments')
    .select('id', { count: 'exact', head: true });

  if (error || countError) {
    console.error('Error fetching comments:', error || countError);
    return { data: [], count: 0 };
  }

  // Fetch user information separately for each comment
  if (data && data.length > 0) {
    for (const comment of data) {
      if (comment.user_id) {
        // Get user information from user_profiles
        const { data: userData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', comment.user_id)
          .single();
          
        if (userData) {
          comment.user = {
            first_name: userData.first_name,
            last_name: userData.last_name
          };
        }
      }
    }
  }

  return { data: data || [], count: count || 0 };
};

export const approveComment = async (id: string) => {
  const { data, error } = await supabase
    .from('blog_comments')
    .update({ approved: true })
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error approving comment:', error);
    return null;
  }

  return data?.[0] || null;
};

export const deleteComment = async (id: string) => {
  const { error } = await supabase
    .from('blog_comments')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting comment:', error);
    return false;
  }

  return true;
};

// Related posts
export const getRelatedPosts = async (postId: string, limit = 3) => {
  // Get the current post's category and tags
  const { data: currentPost } = await supabase
    .from('blog_posts')
    .select('category_id')
    .eq('id', postId)
    .single();

  if (!currentPost) return [];

  const { data: postTags } = await supabase
    .from('blog_post_tags')
    .select('tag_id')
    .eq('post_id', postId);

  const tagIds = postTags ? postTags.map(pt => pt.tag_id) : [];

  // First try to find posts with the same category and tags
  let query = supabase
    .from('blog_posts')
    .select(`
      *,
      category:category_id(name, slug)
    `)
    .eq('published', true)
    .not('id', 'eq', postId)
    .order('published_at', { ascending: false })
    .limit(limit);

  if (currentPost.category_id) {
    query = query.eq('category_id', currentPost.category_id);
  }

  const { data: relatedByCategoryAndTags, error } = await query;

  if (error) {
    console.error('Error fetching related posts:', error);
    return [];
  }

  // Fetch author information for each post
  const fetchAuthorInfo = async (posts: any[]) => {
    if (!posts || posts.length === 0) return;
    
    for (const post of posts) {
      if (post.author_id) {
        const { data: authorData } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', post.author_id)
          .single();
          
        if (authorData) {
          post.author = {
            first_name: authorData.first_name,
            last_name: authorData.last_name
          };
        }
      }
    }
  };

  // If we don't have enough related posts by category, get latest posts
  if (!relatedByCategoryAndTags || relatedByCategoryAndTags.length < limit) {
    const neededPosts = limit - (relatedByCategoryAndTags ? relatedByCategoryAndTags.length : 0);
    
    const { data: latestPosts, error: latestError } = await supabase
      .from('blog_posts')
      .select(`
        *,
        category:category_id(name, slug)
      `)
      .eq('published', true)
      .not('id', 'eq', postId)
      .order('published_at', { ascending: false })
      .limit(neededPosts);
    
    if (!latestError && latestPosts && latestPosts.length > 0) {
      await fetchAuthorInfo(latestPosts);
      
      return [
        ...(relatedByCategoryAndTags || []),
        ...(latestPosts || [])
      ].slice(0, limit);
    }
  }

  if (relatedByCategoryAndTags) {
    await fetchAuthorInfo(relatedByCategoryAndTags);
  }

  return relatedByCategoryAndTags || [];
};

// Blog stats
export const getBlogStats = async () => {
  // Get post count
  const { count: postCount, error: postError } = await supabase
    .from('blog_posts')
    .select('id', { count: 'exact', head: true });

  // Get published post count
  const { count: publishedCount, error: publishedError } = await supabase
    .from('blog_posts')
    .select('id', { count: 'exact', head: true })
    .eq('published', true);

  // Get comment count
  const { count: commentCount, error: commentError } = await supabase
    .from('blog_comments')
    .select('id', { count: 'exact', head: true });

  // Get approved comment count
  const { count: approvedCommentCount, error: approvedError } = await supabase
    .from('blog_comments')
    .select('id', { count: 'exact', head: true })
    .eq('approved', true);

  // Get category count
  const { count: categoryCount, error: categoryError } = await supabase
    .from('blog_categories')
    .select('id', { count: 'exact', head: true });

  // Get tag count
  const { count: tagCount, error: tagError } = await supabase
    .from('blog_tags')
    .select('id', { count: 'exact', head: true });

  if (postError || publishedError || commentError || approvedError || categoryError || tagError) {
    console.error('Error fetching blog stats');
    return null;
  }

  return {
    postCount: postCount || 0,
    publishedCount: publishedCount || 0,
    commentCount: commentCount || 0,
    approvedCommentCount: approvedCommentCount || 0,
    categoryCount: categoryCount || 0,
    tagCount: tagCount || 0
  };
}; 